import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import { 
  FiUsers, 
  FiCalendar, 
  FiDollarSign, 
  FiPackage, 
  FiTool,
  FiFileText,
  FiMail,
  FiSettings,
  FiGrid,
  FiTrendingUp,
  FiShoppingCart,
  FiClipboard
} from '../../components/Icons';

const HomePage: React.FC = () => {
  useEffect(() => {
    const handleOpenSearch = () => {
      const searchButton = document.querySelector('[aria-label="Search"]') as HTMLButtonElement;
      searchButton?.click();
    };

    window.addEventListener('openSearch', handleOpenSearch);
    return () => window.removeEventListener('openSearch', handleOpenSearch);
  }, []);
  const categories = [
    {
      title: 'Getting Started',
      slug: 'getting-started',
      description: 'New to the CRM? Start here to learn the basics',
      icon: <FiSettings className="h-6 w-6" />,
      color: 'bg-blue-500',
      articles: [
        'First Login & Setup',
        'Creating Your Company Profile',
        'Adding Your First Customer',
        'Scheduling Your First Appointment'
      ]
    },
    {
      title: 'Customer Management',
      slug: 'customers',
      description: 'Learn how to manage your customer database effectively',
      icon: <FiUsers className="h-6 w-6" />,
      color: 'bg-green-500',
      articles: [
        'Adding New Customers',
        'Importing Customers via CSV',
        'Managing Customer Information',
        'Customer Communication'
      ]
    },
    {
      title: 'Appointments',
      slug: 'appointments',
      description: 'Schedule and manage appointments with ease',
      icon: <FiCalendar className="h-6 w-6" />,
      color: 'bg-purple-500',
      articles: [
        'Creating Appointments',
        'Calendar Views',
        'Double Booking Prevention',
        'Calendar Sync with Google/Outlook'
      ]
    },
    {
      title: 'Financial Management',
      slug: 'finances',
      description: 'Handle invoices, estimates, and payments',
      icon: <FiDollarSign className="h-6 w-6" />,
      color: 'bg-yellow-500',
      articles: [
        'Creating Invoices',
        'Generating Estimates',
        'Converting Estimates to Invoices',
        'Payment Processing with Stripe'
      ]
    },
    {
      title: 'Orders & Inventory',
      slug: 'orders',
      description: 'Manage orders and track inventory',
      icon: <FiShoppingCart className="h-6 w-6" />,
      color: 'bg-indigo-500',
      articles: [
        'Creating Orders',
        'Order Management',
        'Inventory Tracking',
        'Solar Screen Orders'
      ]
    },
    {
      title: 'Service Management',
      slug: 'services',
      description: 'Configure and manage your service offerings',
      icon: <FiTool className="h-6 w-6" />,
      color: 'bg-red-500',
      articles: [
        'Service Items Setup',
        'Window Cleaning Estimator',
        'Pressure Washing Estimator',
        'Custom Service Creation'
      ]
    },
    {
      title: 'Marketing',
      slug: 'marketing',
      description: 'Manage leads, campaigns, and customer outreach',
      icon: <FiMail className="h-6 w-6" />,
      color: 'bg-pink-500',
      articles: [
        'Lead Management',
        'Creating Marketing Lists',
        'Email & SMS Campaigns',
        'Campaign Templates'
      ]
    },
    {
      title: 'Documents & Reports',
      slug: 'documents',
      description: 'Generate and manage business documents',
      icon: <FiFileText className="h-6 w-6" />,
      color: 'bg-teal-500',
      articles: [
        'Document Management',
        'PDF Generation',
        'Report Creation',
        'Document Templates'
      ]
    },
    {
      title: 'Admin & Settings',
      slug: 'admin',
      description: 'Manage employees, permissions, and system settings',
      icon: <FiGrid className="h-6 w-6" />,
      color: 'bg-gray-600',
      articles: [
        'Employee Management',
        'User Roles & Permissions',
        'Employee Time Tracking',
        'System Configuration'
      ]
    }
  ];

  const popularArticles = [
    { title: 'How to Create Your First Invoice', category: 'finances', slug: 'create-first-invoice' },
    { title: 'Setting Up Calendar Sync', category: 'appointments', slug: 'calendar-sync-setup' },
    { title: 'Importing Customers from CSV', category: 'customers', slug: 'csv-import' },
    { title: 'Managing Todo Lists', category: 'tools', slug: 'todo-lists' },
    { title: 'Creating Marketing Campaigns', category: 'marketing', slug: 'create-campaigns' },
    { title: 'Employee Time Tracking Guide', category: 'admin', slug: 'time-tracking' }
  ];

  return (
    <Layout>
      <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Welcome to the Support Center
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Find answers and learn how to make the most of your CRM
            </p>
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search for help articles..."
                  className="w-full px-6 py-4 rounded-lg text-gray-900 placeholder-gray-500 shadow-lg"
                  onFocus={(e) => {
                    e.target.blur();
                    document.getElementById('search-button')?.click();
                  }}
                />
                <button
                  id="search-button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
                  onClick={() => {
                    // This will be handled by the Layout component
                    const event = new CustomEvent('openSearch');
                    window.dispatchEvent(event);
                  }}
                >
                  Search
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-8 bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900">25+</div>
              <div className="text-sm text-gray-600">Features Documented</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900">100+</div>
              <div className="text-sm text-gray-600">Help Articles</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900">Step-by-Step</div>
              <div className="text-sm text-gray-600">Tutorials</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900">24/7</div>
              <div className="text-sm text-gray-600">Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Browse by Feature</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((category) => (
              <Link
                key={category.slug}
                to={`/categories/${category.slug}`}
                className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow p-6 border border-gray-200"
              >
                <div className="flex items-start space-x-4">
                  <div className={`${category.color} text-white p-3 rounded-lg`}>
                    {category.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {category.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      {category.description}
                    </p>
                    <div className="space-y-1">
                      {category.articles.slice(0, 3).map((article, idx) => (
                        <div key={idx} className="text-xs text-gray-500">
                          • {article}
                        </div>
                      ))}
                      {category.articles.length > 3 && (
                        <div className="text-xs text-blue-600 font-medium">
                          + {category.articles.length - 3} more articles
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Articles */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Popular Articles</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {popularArticles.map((article) => (
              <Link
                key={article.slug}
                to={`/articles/${article.category}/${article.slug}`}
                className="bg-white rounded-lg p-4 hover:shadow-md transition-shadow border border-gray-200"
              >
                <h3 className="font-medium text-gray-900 mb-2">{article.title}</h3>
                <span className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                  {article.category}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Need More Help */}
      <section className="py-12 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Still Need Help?</h2>
          <p className="text-lg text-gray-600 mb-8">
            Can't find what you're looking for? Our support team is here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Contact Support
            </Link>
            <a
              href="mailto:support@definedcleaning.com"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-blue-600 rounded-lg border-2 border-blue-600 hover:bg-blue-50 transition-colors"
            >
              Email Us
            </a>
          </div>
        </div>
      </section>
    </div>
    </Layout>
  );
};

export default HomePage;